// Phase JA.2 — Signal Menu Filter Bar.
//
// Filters: tier · market category · league · min conviction.
// Composes the existing Pill primitive only (no edits to primitives).
// State is held by the parent (URL query params via useSearchParams).

import type { CSSProperties } from "react";
import { Pill } from "../super-acca";
import type {
  MarketCategory,
  SignalMenuFilters,
  TierFilter,
} from "./signalMenuFilters.ts";

const fontMono = "var(--sa-font-mono)";

const TIER_OPTIONS: { id: TierFilter; label: string }[] = [
  { id: "all", label: "ALL" },
  { id: "tier_a", label: "A · HIGH" },
  { id: "tier_b", label: "B · SOLID" },
  { id: "tier_c", label: "C · VALUE" },
];

const CATEGORY_OPTIONS: { id: MarketCategory; label: string }[] = [
  { id: "all", label: "ALL" },
  { id: "1x2", label: "1X2" },
  { id: "goals", label: "GOALS" },
  { id: "btts", label: "BTTS" },
  { id: "cards", label: "CARDS" },
  { id: "corners", label: "CORNERS" },
];

export interface SignalFilterBarProps {
  filters: SignalMenuFilters;
  onChange: (next: SignalMenuFilters) => void;
  /** League options derived from the current signal set. */
  leagueOptions: readonly string[];
  style?: CSSProperties;
}

export function SignalFilterBar({
  filters,
  onChange,
  leagueOptions,
  style,
}: SignalFilterBarProps): JSX.Element {
  return (
    <div
      data-testid="signal-filter-bar"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        padding: 16,
        border: "1px solid var(--sa-hairline)",
        borderRadius: 6,
        background: "var(--sa-bg-raised)",
        ...style,
      }}
    >
      <FilterGroup label="TIER">
        {TIER_OPTIONS.map((opt) => (
          <PillButton
            key={opt.id}
            active={filters.tier === opt.id}
            onClick={() => onChange({ ...filters, tier: opt.id })}
            label={opt.label}
          />
        ))}
      </FilterGroup>
      <FilterGroup label="MARKET">
        {CATEGORY_OPTIONS.map((opt) => (
          <PillButton
            key={opt.id}
            active={filters.category === opt.id}
            onClick={() => onChange({ ...filters, category: opt.id })}
            label={opt.label}
          />
        ))}
      </FilterGroup>
      {leagueOptions.length > 0 ? (
        <FilterGroup label="LEAGUE">
          <PillButton
            active={filters.league === "all"}
            onClick={() => onChange({ ...filters, league: "all" })}
            label="ALL"
          />
          {leagueOptions.map((lg) => (
            <PillButton
              key={lg}
              active={filters.league === lg}
              onClick={() => onChange({ ...filters, league: lg })}
              label={lg.toUpperCase()}
            />
          ))}
        </FilterGroup>
      ) : null}
      <div
        style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 4 }}
      >
        <span
          style={{
            fontFamily: fontMono,
            fontSize: 10,
            letterSpacing: "0.18em",
            color: "var(--sa-fg-muted)",
            minWidth: 84,
          }}
        >
          MIN CONVICTION
        </span>
        <input
          type="range"
          min={0}
          max={100}
          step={5}
          value={filters.minConviction}
          aria-label="Minimum conviction score"
          onChange={(e) =>
            onChange({ ...filters, minConviction: Number(e.target.value) })
          }
          style={{ flex: 1, accentColor: "var(--sa-accent-saffron)" }}
        />
        <span
          style={{
            fontFamily: fontMono,
            fontSize: 12,
            color: "var(--sa-fg-primary)",
            minWidth: 32,
            textAlign: "right",
          }}
        >
          {filters.minConviction}
        </span>
      </div>
    </div>
  );
}

function FilterGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <span
        style={{
          fontFamily: fontMono,
          fontSize: 10,
          letterSpacing: "0.18em",
          color: "var(--sa-fg-muted)",
          minWidth: 84,
        }}
      >
        {label}
      </span>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{children}</div>
    </div>
  );
}

function PillButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        background: "none",
        border: "none",
        padding: 0,
        cursor: "pointer",
      }}
    >
      <Pill
        color={active ? "#0A0A0A" : "var(--sa-fg-secondary)"}
        background={active ? "var(--sa-accent-saffron)" : "transparent"}
        borderColor={active ? "var(--sa-accent-saffron)" : "var(--sa-hairline)"}
      >
        {label}
      </Pill>
    </button>
  );
}
